<template>
    <span>
        <component :is="buttonComponent" ref="helpicon" :class="buttonClasses" type="button" :tabindex="tabindex">
            <b-spinner v-if="loading" small />
            <template v-else>
                <slot name="label">
                    {{ label }}
                </slot>
                <i :class="(iconMuted ? 'text-muted ' : '') + icon" />
            </template>
        </component>
        <b-popover ref="popover" :target="() => helpicon" :triggers="triggers" :placement="placement"
            :custom-class="'search-help ' + popoverCustomClass" :offset="popoverOffset" :delay="delay"
            :boundary="boundary" @show="$emit('show')" @shown="onPopoverShown"
            @hide="$emit('hide')" @hidden="onPopoverHidden">
            <slot :close="closePopover" />
            <VueShowdown v-if="markdown" :markdown="markdown" :options="markdownOptions" class="markdown" />
            <p v-if="text">{{ text }}</p>
        </b-popover>
    </span>

</template>

<script lang="ts">
import { Component, Vue, Ref, Prop } from "vue-property-decorator";

@Component({
    name: "HelpPopup",
})
export default class HelpPopup extends Vue {

    @Prop({ default: "click blur" }) protected triggers?: string;
    @Prop() protected text?: string;
    @Prop({ default: "bottomright" }) protected placement?: string;
    @Prop() protected label?: string;
    @Prop({ default: "fa fa-question-circle" }) protected icon?: string;
    @Prop({ default: true }) protected muted?: boolean;
    @Prop({ default: false }) protected iconMuted?: boolean;
    @Prop({ default: null }) protected variant?: string | null;
    @Prop({ default: "" }) protected popoverCustomClass?: string;
    @Prop({ default: null }) protected markdown?: string | null;
    @Prop({}) protected markdownOptions?: any;
    @Prop({ default: "" }) protected buttonCustomClass?: string;
    @Prop({ default: "button" }) protected buttonComponent!: string;
    @Prop({}) protected popoverOffset?: string | number;
    @Prop({ default: false }) protected loading!: boolean;
    @Prop({}) protected delay?: number | object;
    @Prop({}) protected boundary?: string;
    @Prop({ default: undefined }) protected tabindex?: number;

    @Ref("helpicon") protected helpicon?: any;
    @Ref("popover") protected popover?: any;

    private resizeObserver: ResizeObserver | null = null;

    // Popper.js positions the popover when it opens and does not re-run when the
    // content inside grows (e.g. async-loaded drug details). We observe the
    // rendered popover element and ask BootstrapVue's internal template to
    // reschedule a Popper update whenever its size changes, so the popover stays
    // anchored to the target.
    //
    // BootstrapVue chain: b-popover → $_toolpop (BVTooltip) → $_tip (the popover
    // template that owns the Popper instance) → updatePopper() → scheduleUpdate().
    onPopoverShown() {
        this.$emit("shown");
        this.teardownResizeObserver();
        const tip = this.popover?.$_toolpop?.$_tip;
        const tipEl = tip?.$el as HTMLElement | undefined;
        if (!tip || !tipEl || typeof ResizeObserver === "undefined") return;
        this.resizeObserver = new ResizeObserver(() => {
            tip.updatePopper?.();
        });
        this.resizeObserver.observe(tipEl);
    }

    onPopoverHidden() {
        this.teardownResizeObserver();
        this.$emit("hidden");
    }

    beforeDestroy() {
        this.teardownResizeObserver();
    }

    private teardownResizeObserver() {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
    }

    get buttonClasses() {
        const classes = [];

        if (this.buttonComponent === "button") classes.push("default");
        if (this.label || this.$slots.label) classes.push("hasLabel");
        if (this.muted) classes.push("text-muted");

        if (this.variant) {
            classes.push(`text-${this.variant}`);
        }

        if (this.buttonCustomClass) {
            classes.push(this.buttonCustomClass);
        }

        return classes;
    }

    closePopover() {
        this.$root.$emit('bv::hide::popover');
    }

}

</script>

<style scoped lang="scss">
button.default {
    border: 0;
    background: transparent;
    padding: 0 5px;

    &.hasLabel {
        background: rgb(248, 248, 248);
        border: 1px solid rgb(211, 211, 211);
        border-radius: 4px;

        &:hover {
            transition: all .2s;
            background: rgb(240, 240, 240);
            color: black !important;
        }
    }
}

.search-help {
    line-height: 1.25;

    ::v-deep(.popover-body > *:last-child),
    ::v-deep(.popover-body .markdown > *:last-child) {
        margin-bottom: 0;
    }

    ::v-deep(ul) {
        margin-bottom: 5px;
        padding-left: 20px;
    }
}
</style>